import React from 'react';
import { Helmet } from 'react-helmet'; // Import Helmet for SEO
import { CheckCircle, Gift, Layers, PenTool, ShieldCheck, Star, Users, Home, Ruler, Hammer } from 'lucide-react';
import WaterproofingAboutSection from './WaterproofingAboutSection';

const About = () => {
  // SEO: Schema Markup Updated for Multi-City Presence
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": "Luxuriya Homes",
    "image": "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80",
    "description": "Top-rated interior design firm in Delhi, Lucknow & Varanasi specializing in luxury home renovations and turnkey execution.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Noida, Delhi NCR, Lucknow, Varanasi", 
      "addressRegion": "Uttar Pradesh",
      "addressCountry": "IN"
    },
    "priceRange": "$$$",
    "openingHours": "Mo-Sa 09:00-18:00",
    "telephone": "+91-7291832000" // Updated to your number
  };

  return (
    <div className="bg-white font-sans text-neutral-900">
      
      {/* --- SEO META TAGS --- */}
      <Helmet>
        <title>About Us | Best Interior Designers in Delhi, Lucknow & Varanasi</title>
        <meta name="description" content="Luxuriya Homes is a premium interior design firm serving Delhi NCR, Lucknow, and Varanasi. We offer 3D visualization, turnkey execution, and 10-year warranty." />
        <meta name="keywords" content="interior designers in lucknow, luxury interiors delhi, home renovation varanasi, turnkey contractors noida, best architects in UP" />
        <link rel="canonical" href="https://www.luxuriyahomes.com/about" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About Luxuriya Homes - Premier Designers in Delhi & UP" />
        <meta property="og:description" content="We build homes that reflect your legacy. Serving Noida, Lucknow & Varanasi since 2010." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      {/* 1. HERO SECTION */}
      <header className="relative h-[60vh] min-h-[500px] flex items-center justify-center bg-neutral-900" role="banner">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-50" 
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80')" }}
          role="img"
          aria-label="Luxury Living Room Interior Design in Delhi"
        ></div>
        <div className="relative z-10 text-center px-4 pt-20 animate-fadeIn">
          <span className="text-amber-400 tracking-[0.3em] text-xs md:text-sm font-bold uppercase mb-4 block">
             Est. 2010 • Serving Delhi, Lucknow & Varanasi
          </span>
          <h1 className="text-4xl md:text-6xl font-serif text-white mb-6 tracking-wide leading-tight">
            About Luxuriya Homes
          </h1>
          <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
          <p className="text-gray-200 text-sm md:text-lg max-w-2xl mx-auto font-light leading-relaxed">
            Where transparency meets timeless design. We build homes that reflect your legacy across North India.
          </p>
        </div>
      </header>

      {/* 2. WHO WE ARE */}
      <section className="py-24 px-6 max-w-7xl mx-auto" aria-labelledby="identity-heading">
        <div className="flex flex-col md:flex-row gap-16 items-center">
          <div className="w-full md:w-1/2">
            <h4 className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-3">Our Identity</h4>
            <h2 id="identity-heading" className="text-3xl md:text-5xl font-serif mb-6 leading-tight text-neutral-900">
              More Than Just Interiors, <br/> We Build <span className="italic text-gray-500">Lifestyles.</span>
            </h2>
            <p className="text-gray-600 leading-8 mb-6 text-lg font-light">
              Luxuriya Homes is a premium <strong>interior design firm</strong> specializing in transforming bare shells in Noida, Lucknow, and Varanasi into royal sanctuaries. Unlike traditional contractors, we offer a fully managed renovation experience.
            </p>
            <p className="text-gray-600 leading-8 text-lg font-light">
              From the first sketch to the final curtain, we handle everything. Our team consists of certified <strong>architects and skilled craftsmen</strong> who ensure your home in Delhi or UP looks exactly like the 3D design.
            </p>
          </div>
          
          <div className="w-full md:w-1/2 grid grid-cols-2 gap-6">
             {[
               { icon: <Users size={28}/>, num: "150+", label: "Happy Families" },
               { icon: <ShieldCheck size={28}/>, num: "10 Yrs", label: "Flat Warranty" },
               { icon: <Star size={28}/>, num: "4.9/5", label: "Google Rating" },
               { icon: <PenTool size={28}/>, num: "25+", label: "Design Experts" }
             ].map((stat, idx) => (
               <div key={idx} className="bg-neutral-50 p-8 rounded-sm text-center border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
                  <div className="text-amber-600 mb-3 flex justify-center group-hover:scale-110 transition-transform">{stat.icon}</div>
                  <h3 className="font-serif font-bold text-2xl mb-1">{stat.num}</h3>
                  <p className="text-xs text-gray-500 uppercase tracking-widest">{stat.label}</p>
               </div>
             ))}
          </div>
        </div>
      </section>

      {/* 3. THE JOURNEY */}
      <section className="bg-neutral-900 text-white py-24 px-6 overflow-hidden relative" aria-labelledby="process-heading">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-600/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <span className="text-amber-500 tracking-[0.2em] text-xs font-bold uppercase mb-2 block">Our Process</span>
            <h2 id="process-heading" className="text-3xl md:text-5xl font-serif">Your Journey to a Dream Home</h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              We have simplified interior design for our Delhi & UP clients into 4 transparent steps.
            </p>
          </div>

          {/* Timeline Connector */}
          <div className="hidden md:block absolute top-[45%] left-10 right-10 h-[2px] bg-gray-800 z-0"></div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10">
            {[
              { 
                step: "01", 
                title: "Meet & Discover", 
                desc: "We visit your site in Delhi, Lucknow, or Varanasi to understand your lifestyle needs and discuss budget.",
                img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=600",
                icon: <Users size={20} />
              },
              { 
                step: "02", 
                title: "Design & Visualize", 
                desc: "Our architects create photorealistic 3D renders. You see exactly how your home will look before we start.",
                img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=600",
                icon: <Ruler size={20} />
              },
              { 
                step: "03", 
                title: "Execution", 
                desc: "Materials are cut in our factory for precision. Our local teams handle civil work and installation.",
                img: "https://ohwoodworks.com/wp-content/uploads/2023/08/Carpentry-and-Joinery-in-Dubai-1.jpg", 
                icon: <Hammer size={20} />
              },
              { 
                step: "04", 
                title: "The Grand Reveal", 
                desc: "We perform deep cleaning, final quality checks, and hand over the keys with warranty documents.",
                img: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&q=80&w=600",
                icon: <Home size={20} />
              },
            ].map((item, idx) => (
              <div key={idx} className="group relative bg-neutral-900 md:bg-transparent">
                <div className="h-56 overflow-hidden rounded-sm mb-6 border-b-4 border-amber-600 relative shadow-2xl">
                  <img 
                    src={item.img} 
                    alt={`Luxuriya Homes Process Step ${item.step}: ${item.title}`} 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    loading="lazy" 
                  />
                  <div className="absolute top-4 left-4 bg-white text-black font-serif font-bold text-xl w-10 h-10 flex items-center justify-center rounded-full z-20 shadow-lg border-2 border-amber-600">
                    {item.step}
                  </div>
                </div>
                <div className="bg-neutral-800/50 md:bg-transparent p-4 md:p-0 rounded-lg md:rounded-none">
                  <h3 className="text-xl font-serif font-bold mb-3 flex items-center gap-2 text-white">
                    <span className="text-amber-500">{item.icon}</span> {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed border-l-2 border-gray-700 pl-4 group-hover:border-amber-600 group-hover:text-gray-300 transition-colors duration-300">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. MATERIALS */}
      <section className="bg-white py-24 px-6" aria-labelledby="materials-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 id="materials-heading" className="text-3xl font-serif mb-4 text-neutral-900">What Goes Inside Your Home?</h2>
            <p className="text-gray-500">We use only standardized, branded materials for all our projects in UP & NCR.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 border border-gray-100 shadow-sm hover:shadow-xl transition-all bg-neutral-50 rounded-lg">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-amber-700">
                <Layers size={20}/> Core Materials
              </h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5"/> <span><strong>Plywood:</strong> Greenply / Century (BWR Grade)</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5"/> <span><strong>Hardware:</strong> Hettich / Hafele (Soft Close)</span></li>
              </ul>
            </div>
            
            <div className="p-8 border border-gray-100 shadow-sm hover:shadow-xl transition-all bg-neutral-50 rounded-lg">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-amber-700">
                <PenTool size={20}/> Finishes
              </h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5"/> <span><strong>Paint:</strong> Asian Paints Royale</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5"/> <span><strong>Polish:</strong> Italian High Gloss PU</span></li>
              </ul>
            </div>

            <div className="p-8 border border-gray-100 shadow-sm hover:shadow-xl transition-all bg-neutral-50 rounded-lg">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-amber-700">
                <ShieldCheck size={20}/> Essentials
              </h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5"/> <span><strong>Wires:</strong> Havells (Fire Retardant)</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5"/> <span><strong>Sanitary:</strong> Kohler / Jaquar</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CUSTOMER GUIDE */}
      <section className="bg-neutral-50 py-20 px-6 border-t border-gray-200">
        <div className="max-w-5xl mx-auto">
            <div className="mb-10 text-center md:text-left">
                <h4 className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-2">Customer Guide</h4>
                <h2 className="text-3xl font-serif text-neutral-900">How to Choose Your Design?</h2>
            </div>

            <div className="space-y-4">
                <article className="bg-white p-6 rounded-lg shadow-sm flex flex-col md:flex-row gap-6 items-start">
                    <span className="text-4xl font-serif text-amber-200 font-bold">01</span>
                    <div>
                        <h3 className="text-xl font-bold mb-2">Identify Your Usage</h3>
                        <p className="text-gray-600 text-sm">
                            High traffic home (Kids/Pets)? Go for <strong>Matte Laminates</strong> (Scratch resistant). <br/>
                            Luxury look required? Go for <strong>High Gloss PU</strong> or <strong>Acrylic</strong>.
                        </p>
                    </div>
                </article>

                <article className="bg-white p-6 rounded-lg shadow-sm flex flex-col md:flex-row gap-6 items-start">
                    <span className="text-4xl font-serif text-amber-200 font-bold">02</span>
                    <div>
                        <h3 className="text-xl font-bold mb-2">Select a Theme</h3>
                        <p className="text-gray-600 text-sm">
                            <strong>Modern:</strong> Minimal, Clean Lines. | <strong>Classic:</strong> Ornamental, Warm. | <strong>Contemporary:</strong> Bold, Artistic.
                        </p>
                    </div>
                </article>
            </div>
        </div>
      </section>

      <WaterproofingAboutSection />

      {/* 6. REFERRAL */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
         <div className="bg-neutral-900 text-white rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-600 rounded-full blur-[80px] opacity-20"></div>
            
            <div className="bg-amber-600 p-5 rounded-full z-10">
                <Gift size={32} className="text-white"/>
            </div>
            
            <div className="flex-1 z-10 text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-serif mb-3">Refer & Earn Program</h2>
                <p className="text-gray-300 text-sm md:text-base max-w-xl">
                    Know someone looking for interiors in Delhi, Lucknow or Varanasi? Refer them to Luxuriya Homes and earn exclusive rewards.
                </p>
            </div>

            <button className="bg-white text-neutral-900 px-8 py-3 text-xs font-bold uppercase tracking-widest hover:bg-amber-500 hover:text-white transition-all z-10">
                Refer Now
            </button>
         </div>
      </section>

    </div>
  );
};

export default About;